import { useState, useEffect } from "react";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  FileText,
  Download,
  File,
  Image,
  Video,
  FileSpreadsheet,
  BookOpen,
  Calendar,
} from "lucide-react";

const API = `${window.location.origin}/api`;

const getFileIcon = (fileType) => {
  if (fileType?.includes("pdf")) return <FileText className="w-5 h-5 text-red-500" />;
  if (fileType?.includes("image")) return <Image className="w-5 h-5 text-blue-500" />;
  if (fileType?.includes("video")) return <Video className="w-5 h-5 text-purple-500" />;
  if (fileType?.includes("spreadsheet") || fileType?.includes("excel")) return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
  return <File className="w-5 h-5 text-gray-500" />;
};

const MyMaterialsPage = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/my-materials`);
      setMaterials(response.data || []);
    } catch (error) {
      toast.error("Errore nel caricamento dei materiali");
    } finally {
      setLoading(false);
    }
  };

  // Group materials by session
  const materialsBySession = materials.reduce((acc, material) => {
    const sessionId = material.session_id;
    if (!acc[sessionId]) {
      acc[sessionId] = {
        session_title: material.session_title,
        materials: [],
      };
    }
    acc[sessionId].materials.push(material);
    return acc;
  }, {});

  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="mb-8">
          <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-2">
            I Miei Materiali
          </h1>
          <p className="text-muted-foreground">
            Dispense e materiali delle sessioni a cui sei iscritto
          </p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-secondary rounded-xl animate-pulse" />
            ))}
          </div>
        ) : Object.keys(materialsBySession).length > 0 ? (
          <div className="space-y-6">
            {Object.entries(materialsBySession).map(([sessionId, { session_title, materials: sessionMaterials }]) => (
              <Card key={sessionId} className="rounded-2xl border-border/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="font-medium text-lg">{session_title}</h3>
                  </div>
                  
                  <div className="space-y-3">
                    {sessionMaterials.map((material) => (
                      <div
                        key={material.material_id}
                        className="flex items-center gap-4 p-3 bg-secondary/50 rounded-xl"
                        data-testid={`material-${material.material_id}`}
                      >
                        <div className="w-10 h-10 rounded-lg bg-background flex items-center justify-center">
                          {getFileIcon(material.file_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium truncate">{material.name}</h4>
                          <p className="text-xs text-muted-foreground">
                            {material.file_type?.split("/")[1]?.toUpperCase() || "FILE"}
                          </p>
                        </div>
                        <a href={material.file_url} target="_blank" rel="noopener noreferrer">
                          <Button
                            variant="outline"
                            size="sm"
                            className="rounded-full"
                            data-testid={`download-${material.material_id}`}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Scarica
                          </Button>
                        </a>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="rounded-2xl border-border/50">
            <CardContent className="py-16 text-center">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="font-medium mb-2">Nessun materiale disponibile</h3>
              <p className="text-muted-foreground text-sm mb-4">
                I materiali saranno disponibili dopo l'iscrizione alle sessioni formative
              </p>
              <Button
                onClick={() => window.location.href = "/sessioni"}
                className="rounded-full"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Vedi Sessioni
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Info card */}
        <Card className="rounded-2xl border-border/50 mt-6 bg-primary/5">
          <CardContent className="p-6">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Informazioni sui Materiali
            </h3>
            <p className="text-sm text-muted-foreground">
              I materiali didattici sono disponibili solo per le sessioni a cui sei iscritto.
              Dopo aver completato l'iscrizione, potrai scaricare dispense, PDF, video e altri
              contenuti formativi utili per la tua preparazione.
            </p>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

export default MyMaterialsPage;
